import { useState, useEffect, useMemo } from "react";
import { useParams, Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowLeft, Star, Waves, MapPin, ChevronRight, ChevronLeft, Check, Languages, X, Share2, Images, Search, Loader2,
  Wifi, Car, Umbrella, Snowflake, Flower2, UtensilsCrossed, PawPrint, CookingPot, Eye, Dumbbell, Coffee, Baby,
} from "lucide-react";
import { toast } from "sonner";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { PropertiesMap } from "@/components/PropertiesMap";
import { FlashTimer } from "@/components/FlashTimer";
import { FavButton } from "@/components/FavButton";
import { BookingModal } from "@/components/BookingModal";
import { PriceHistory } from "@/components/PriceHistory";
import { useApp } from "@/context/AppContext";
import { getProperty, getConfig } from "@/lib/api";
import { tr } from "@/i18n/translations";
import { euro, providerName, PROVIDER_META, bookingImg } from "@/lib/format";
import { Seo, truncate } from "@/lib/seo";
import { BrandTag } from "@/components/BrandTag";
import { DateRangePicker } from "@/components/DateRangePicker";
import { GuestsPicker } from "@/components/GuestsPicker";

const ICONS = { Wifi, Waves, Car, Umbrella, Snowflake, Flower2, UtensilsCrossed, PawPrint, CookingPot, Eye, Dumbbell, Coffee, Baby };
const AUTO_LANGS = ["de", "hu", "cs", "sl"];

export default function PropertyDetail() {
  const { slug } = useParams();
  const { t, lang } = useApp();
  const [params, setParams] = useSearchParams();
  const [p, setP] = useState(null);
  const [amenityMeta, setAmenityMeta] = useState([]);
  const [active, setActive] = useState(0);
  const [bookingOpen, setBookingOpen] = useState(false);
  const [galleryOpen, setGalleryOpen] = useState(false);
  const [affiliate, setAffiliate] = useState(null);
  const [availCi, setAvailCi] = useState(params.get("check_in") || "");
  const [availCo, setAvailCo] = useState(params.get("check_out") || "");
  const [availGuests, setAvailGuests] = useState(Number(params.get("guests")) || 2);
  const [availChildAges, setAvailChildAges] = useState(
    params.get("children_ages") ? params.get("children_ages").split(",").map(Number) : []
  );
  const [refetching, setRefetching] = useState(false);
  const [pendingCheck, setPendingCheck] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  useEffect(() => {
    setRefetching(true);
    getProperty(slug, {
      check_in: params.get("check_in") || "",
      check_out: params.get("check_out") || "",
      guests: params.get("guests") || "",
      children_ages: params.get("children_ages") || "",
      lang,
    })
      .then(setP)
      .catch(() => setP(false))
      .finally(() => {
        setRefetching(false);
        setPendingCheck((prev) => {
          if (prev) toast.success(t("detail.availability_updated"));
          return false;
        });
      });
    getConfig().then((cfg) => {
      setAmenityMeta(cfg.amenities);
      setAffiliate(cfg.affiliate);
    }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug, params, lang]);

  const imgs = (p && p.images) || [];
  useEffect(() => {
    if (!galleryOpen) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") setGalleryOpen(false);
      if (e.key === "ArrowRight") setActive((a) => (a + 1) % imgs.length);
      if (e.key === "ArrowLeft") setActive((a) => (a - 1 + imgs.length) % imgs.length);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [galleryOpen, imgs.length]);

  const seo = useMemo(() => {
    if (!p) return null;
    const zone = p.zone_name || "Umago";
    const title = `${p.name} — ${zone}, Umago | Umago Best Price`;
    const fallback = `${p.name}, ${zone} (Umago, Istria). Confronta i prezzi live e prenota al miglior prezzo.`;
    const description = truncate(p.description_full || tr(p.desc, lang) || fallback, 158);
    const image = (p.images && p.images[0]) || undefined;
    const url = `${window.location.origin}/stay/${p.slug}`;
    const ld = {
      "@context": "https://schema.org",
      "@type": p.type === "hotel" ? "Hotel" : "LodgingBusiness",
      name: p.name,
      description: p.description_full || fallback,
      url,
      address: { "@type": "PostalAddress", addressLocality: "Umag", addressRegion: "Istria", addressCountry: "HR" },
    };
    if (image) ld.image = (p.images || []).slice(0, 6);
    if (p.lat != null && p.lng != null) ld.geo = { "@type": "GeoCoordinates", latitude: p.lat, longitude: p.lng };
    if (p.best_price) ld.priceRange = `€${Math.round(p.best_price)}`;
    if (p.stars) ld.starRating = { "@type": "Rating", ratingValue: p.stars };
    return { title, description, image, url, jsonLd: JSON.stringify(ld) };
  }, [p, lang]);

  if (p === false)
    return (
      <div className="flex min-h-screen items-center justify-center bg-sand">
        <p className="font-serif text-2xl text-petrol">404 · Not found</p>
      </div>
    );
  if (!p)
    return <div className="min-h-screen animate-pulse bg-sand" />;

  const providerRows = ["nuitee", "holidu", "booking", "direct"]
    .filter((k) => p.prices?.[k] != null)
    .map((k) => ({ id: k, price: p.prices?.[k] }));
  const isFlash = p.flash_deal?.active;

  const book = (id) => {
    if (id === "booking") {
      // Deep-link straight to this hotel's page on Booking.com.
      // dest_id = Booking hotel id + dest_type=hotel targets the exact property
      // (a free-text search alone gets redirected to Booking's homepage).
      const ci = params.get("check_in") || "";
      const co = params.get("check_out") || "";
      const guests = params.get("guests") || "";
      const kidsAges = (params.get("children_ages") || "").split(",").filter((a) => a !== "");
      const q = new URLSearchParams({ ss: p.name });
      if (p.booking_id != null) {
        q.set("dest_id", String(p.booking_id));
        q.set("dest_type", "hotel");
      }
      if (ci && co) {
        q.set("checkin", ci);
        q.set("checkout", co);
      }
      q.set("group_adults", guests || "2");
      q.set("no_rooms", "1");
      if (kidsAges.length) {
        q.set("group_children", String(kidsAges.length));
        kidsAges.forEach((a) => q.append("age", a));
      }
      window.open(`https://www.booking.com/searchresults.html?${q.toString()}`, "_blank", "noopener,noreferrer");
      return;
    }
    // bestprice (nuitee) and any other direct rate → our Stripe checkout
    setBookingOpen(true);
  };

  const prevPhoto = () => setActive((a) => (a - 1 + imgs.length) % imgs.length);
  const nextPhoto = () => setActive((a) => (a + 1) % imgs.length);
  const share = async () => {
    const url = window.location.href;
    try {
      if (navigator.share) await navigator.share({ title: p.name, url });
      else {
        await navigator.clipboard.writeText(url);
        toast.success(t("detail.link_copied"));
      }
    } catch { /* user cancelled */ }
  };
  const typeLabel = p.type === "hotel" ? t("detail.type_hotel") : t("detail.type_apartment");

  const checkAvailability = () => {
    const next = new URLSearchParams(params);
    if (availCi) next.set("check_in", availCi); else next.delete("check_in");
    if (availCo) next.set("check_out", availCo); else next.delete("check_out");
    next.set("guests", String(availGuests));
    if (availChildAges.length) next.set("children_ages", availChildAges.join(",")); else next.delete("children_ages");
    setPendingCheck(true);
    setParams(next);
  };

  const amenityLabel = (id) => {
    const a = amenityMeta.find((x) => x.id === id);
    return a ? a[lang] || a.en : id;
  };
  const amenityIcon = (id) => {
    const a = amenityMeta.find((x) => x.id === id);
    return ICONS[a?.icon] || Check;
  };

  return (
    <div className="min-h-screen overflow-x-hidden bg-sand" data-testid="property-detail-modal">
      {seo && <Seo {...seo} />}
      <Navbar />

      {/* Full-width photo hero */}
      <section className="relative h-[64vh] min-h-[460px] w-full overflow-hidden bg-petrol" data-testid="property-hero">
        <motion.img
          key={active}
          initial={{ opacity: 0, scale: 1.04 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.7 }}
          src={imgs[active]} alt={p.name}
          loading="eager" fetchPriority="high" decoding="async"
          className="absolute inset-0 h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-petrol via-petrol/25 to-petrol/50" />

        {/* Top controls (below the transparent navbar) */}
        <div className="absolute inset-x-0 top-20 z-20 mx-auto flex max-w-7xl items-center justify-between px-5 lg:px-8">
          <Link to="/stays" data-testid="detail-back" className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-white/90 transition-colors hover:text-gold">
            <ArrowLeft size={16} /> {t("detail.back")}
          </Link>
          <div className="flex items-center gap-2">
            <button
              onClick={share} data-testid="detail-share"
              className="flex items-center gap-2 rounded-none bg-white/15 px-3 py-2 text-xs font-semibold uppercase tracking-wide text-white backdrop-blur transition-colors hover:bg-white/30"
            >
              <Share2 size={14} /> {t("detail.share")}
            </button>
            <FavButton slug={p.slug} className="!rounded-none shadow-md" />
          </div>
        </div>

        {/* Photo arrows */}
        {imgs.length > 1 && (
          <>
            <button onClick={prevPhoto} data-testid="hero-prev" aria-label="previous photo"
              className="absolute left-4 top-1/2 z-20 flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-none bg-white/15 text-white backdrop-blur transition-colors hover:bg-white/30">
              <ChevronLeft size={22} />
            </button>
            <button onClick={nextPhoto} data-testid="hero-next" aria-label="next photo"
              className="absolute right-4 top-1/2 z-20 flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-none bg-white/15 text-white backdrop-blur transition-colors hover:bg-white/30">
              <ChevronRight size={22} />
            </button>
          </>
        )}

        {/* Hero title block + open-gallery button */}
        <div className="absolute inset-x-0 bottom-0 z-20 mx-auto max-w-7xl px-5 pb-8 lg:px-8">
          <div className="flex flex-wrap items-end justify-between gap-4">
            <div>
              <div className="flex items-center gap-2">
                <span className="rounded-none bg-terracotta px-3 py-1 text-[0.7rem] font-bold uppercase tracking-widest text-white">{typeLabel}</span>
                {isFlash && (
                  <span className="rounded-none bg-gold px-3 py-1 text-petrol">
                    <FlashTimer endsAt={p.flash_deal.ends_at} label={t("card.ends_in")} />
                  </span>
                )}
              </div>
              <h1 className="mt-3 font-serif text-4xl font-semibold text-white drop-shadow lg:text-6xl">{p.name}</h1>
              <div className="mt-3 flex flex-wrap items-center gap-4 text-sm text-white/85">
                <span className="flex items-center gap-1">
                  {Array.from({ length: p.stars }).map((_, i) => (
                    <Star key={i} size={14} className="fill-gold text-gold" />
                  ))}
                </span>
                <span className="flex items-center gap-1.5"><MapPin size={14} /> {p.zone_name}</span>
                {p.distance_beach != null && (
                  <span className="flex items-center gap-1.5"><Waves size={14} /> {p.distance_beach}m {t("card.to_beach")}</span>
                )}
              </div>
            </div>
            {imgs.length > 1 && (
              <button
                onClick={() => setGalleryOpen(true)} data-testid="open-gallery"
                className="flex items-center gap-2 rounded-none bg-white px-4 py-2.5 text-sm font-semibold text-petrol shadow-lg transition-colors hover:bg-gold"
              >
                <Images size={16} /> {t("detail.all_photos")} · {imgs.length}
              </button>
            )}
          </div>
        </div>
      </section>

      {/* Thumbnail strip */}
      {imgs.length > 1 && (
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <div data-testid="hero-thumb-strip" className="flex gap-2 overflow-x-auto py-4">
            {imgs.map((img, i) => (
              <button
                key={i} data-testid={`gallery-thumb-${i}`} onClick={() => setActive(i)}
                className={`h-16 w-24 flex-shrink-0 overflow-hidden rounded-none border-2 transition-all ${active === i ? "border-terracotta" : "border-transparent opacity-70 hover:opacity-100"}`}
              >
                <img src={bookingImg(img, "square240")} alt="" loading="lazy" decoding="async" className="h-full w-full object-cover" />
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="mx-auto max-w-7xl px-5 lg:px-8">
        <div className="mt-6 grid grid-cols-1 gap-10 lg:grid-cols-[1fr_360px]">
          {/* Left column */}
          <div>
            {p.combined_rating != null && (
              <div data-testid="combined-rating" className="mb-5 inline-flex items-center gap-3 border border-sand-border bg-white px-4 py-2.5">
                <span className="font-serif text-3xl font-bold leading-none text-petrol">
                  {p.combined_rating}<span className="text-base font-normal text-petrol/40">/10</span>
                </span>
                <span className="h-8 w-px bg-sand-border" />
                <span className="text-xs leading-tight text-petrol/60">
                  <span className="block font-semibold text-petrol/80">{t("detail.combined_title")}</span>
                  {p.combined_source_names} · {p.combined_reviews_total} {t("card.reviews")}
                </span>
              </div>
            )}
            <div className="flex flex-wrap items-center gap-4 text-sm text-petrol/60">
              <span className="flex items-center gap-1.5" data-testid="booking-rating">
                <span className="rounded-none bg-emerald-600 px-2 py-0.5 font-bold text-white">{p.rating}</span>
                {p.reviews_count} {t("card.reviews")} <span className="text-petrol/60">Booking.com</span>
              </span>
              {p.google_rating != null && (
                <span className="flex items-center gap-1.5" data-testid="google-rating">
                  <span className="flex items-center gap-1 rounded-none border border-sand-border bg-white px-2 py-0.5 font-bold text-petrol">
                    <img src="https://www.google.com/favicon.ico" alt="Google" className="h-3.5 w-3.5" /> {p.google_rating}
                  </span>
                  {p.google_rating_count} {t("card.reviews")} <span className="text-petrol/60">Google</span>
                </span>
              )}
              <span className="flex items-center gap-1.5"><MapPin size={14} /> Umago, Istria</span>
            </div>

            {tr(p.desc, lang) ? (
              <>
                <p className="mt-6 text-lg leading-relaxed text-petrol/75">{tr(p.desc, lang)}</p>
                {AUTO_LANGS.includes(lang) && p.auto_translated && (
                  <span data-testid="auto-translated-badge" className="mt-2 inline-flex items-center gap-1.5 rounded-none bg-sage/15 px-2.5 py-1 text-xs font-medium text-sage-dark">
                    <Languages size={12} /> {t("detail.auto_translated")}
                  </span>
                )}
              </>
            ) : null}

            <PriceHistory slug={p.slug} />

            {/* Amenities */}
            {p.amenities?.length > 0 && (
              <div className="mt-10" data-testid="amenities-section">
                <h2 className="font-serif text-2xl font-semibold text-petrol">{t("detail.amenities")}</h2>
                <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-3">
                  {p.amenities.map((id) => {
                    const Icon = amenityIcon(id);
                    return (
                      <div key={id} data-testid={`amenity-${id}`} className="flex items-center gap-2.5 rounded-xl border border-sand-border bg-sand-card px-3 py-2.5 text-sm text-petrol">
                        <Icon size={17} className="text-terracotta" /> {amenityLabel(id)}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Rich description (Booking or admin override) */}
            {p.description_full && (
              <div className="mt-10" data-testid="description-section">
                <h2 className="font-serif text-2xl font-semibold text-petrol">{t("detail.description")}</h2>
                <p className="mt-4 whitespace-pre-line text-base leading-relaxed text-petrol/75" data-testid="description-text">{p.description_full}</p>
                {p.description_auto_translated && (
                  <span data-testid="description-auto-badge" className="mt-2 inline-flex items-center gap-1.5 rounded-none bg-sage/15 px-2.5 py-1 text-xs font-medium text-sage-dark">
                    <Languages size={12} /> {t("detail.auto_translated")}
                  </span>
                )}
              </div>
            )}

            {/* Reviews */}
            <div className="mt-10">
              <h2 className="font-serif text-2xl font-semibold text-petrol">{t("detail.reviews")}</h2>
              {p.reviews?.length > 0 ? (
                <>
                  {p.reviews_auto_translated && (
                    <span data-testid="auto-translated-badge-reviews" className="mt-1 inline-flex items-center gap-1.5 rounded-none bg-sage/15 px-2.5 py-1 text-xs font-medium text-sage-dark">
                      <Languages size={12} /> {t("detail.auto_translated")}
                    </span>
                  )}
                  <div className="mt-4 flex flex-col gap-4">
                    {p.reviews.map((r, i) => (
                      <motion.div
                        key={i}
                        initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
                        className="rounded-2xl border border-sand-border bg-sand-card p-5"
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <div className="flex h-10 w-10 items-center justify-center rounded-none bg-petrol font-serif text-lg text-sand">
                              {r.author[0]}
                            </div>
                            <div>
                              <p className="font-semibold text-petrol">{r.author}</p>
                              <p className="text-xs text-petrol/50">{r.country} · {r.date}</p>
                            </div>
                          </div>
                          {r.score && <span className="rounded-md bg-emerald-600 px-2 py-0.5 text-sm font-bold text-white">{r.score}</span>}
                        </div>
                        <p className="mt-3 text-petrol/75">{tr(r.text, lang)}</p>
                        {r.cons && tr(r.cons, lang) && (
                          <p className="mt-2 flex flex-wrap gap-1.5 text-sm text-petrol/55">
                            <span className="font-semibold text-terracotta">− {t("detail.review_cons")}:</span>
                            {tr(r.cons, lang)}
                          </p>
                        )}
                      </motion.div>
                    ))}
                  </div>
                  <Link
                    to={`/stay/${slug}/reviews`}
                    data-testid="see-all-reviews"
                    className="mt-5 inline-flex items-center gap-2 rounded-none border border-petrol/20 px-5 py-2.5 text-sm font-semibold text-petrol transition-colors hover:border-terracotta hover:text-terracotta"
                  >
                    {t("detail.all_reviews")} ({p.reviews_count})
                  </Link>
                </>
              ) : (
                p.rating && (
                  <div data-testid="review-aggregate" className="mt-4 flex items-center gap-5 rounded-2xl border border-sand-border bg-sand-card p-6">
                    <span className="flex h-16 w-16 items-center justify-center rounded-2xl bg-emerald-600 font-serif text-2xl font-bold text-white">
                      {p.rating}
                    </span>
                    <div>
                      {p.review_word && <p className="font-serif text-xl font-semibold text-petrol">{p.review_word}</p>}
                      <p className="text-sm text-petrol/70">
                        {p.reviews_count} {t("card.reviews")} · {t("detail.reviews_source")}
                      </p>
                    </div>
                  </div>
                )
              )}
            </div>

            {/* Location */}
            <div className="mt-10">
              <h2 className="mb-4 font-serif text-2xl font-semibold text-petrol">{t("detail.location")}</h2>
              <PropertiesMap
                points={[{ ...p }]}
                center={[p.lat, p.lng]}
                zoom={15}
                height="360px"
                onlyOne
              />
            </div>
          </div>

          {/* Right sticky comparison */}
          <div>
            <div className="sticky top-24 rounded-2xl border border-sand-border bg-sand-card p-6">
              <p className="overline text-emerald-600" style={{ fontSize: "0.6rem" }}>{t("card.best_price")}{p.best_provider ? ` · ${p.best_provider === "nuitee" ? "bestprice" : providerName(p.best_provider)}` : ""}</p>
              <div className="mt-1 flex items-baseline gap-2">
                <span className="font-serif text-4xl font-bold text-petrol">{euro(p.best_price)}</span>
                <span className="text-sm text-petrol/50">{t("card.per_night")}</span>
              </div>
              {p.savings > 0 && (
                <p className="mt-1 text-sm font-semibold text-terracotta">{t("card.save")} {euro(p.savings)}</p>
              )}
              {p.nights ? (
                <p data-testid="detail-total-price" className="mt-1 text-sm text-petrol/60">
                  {euro(p.total)} {t("search.total_for")} {p.nights} {t("search.nights")}
                </p>
              ) : null}

              {/* Availability search */}
              <div className="mt-5 border border-sand-border bg-white/70 p-3" data-testid="availability-bar">
                <p className="overline mb-2 text-petrol/60">{t("detail.check_availability")}</p>
                <DateRangePicker
                  ci={availCi}
                  co={availCo}
                  onChange={(a, b) => { setAvailCi(a); setAvailCo(b); }}
                  triggerClassName="flex w-full items-center gap-3 border border-sand-border bg-white px-3 py-2.5 text-left text-sm"
                />
                <div className="mt-2">
                  <GuestsPicker
                    adults={availGuests}
                    childrenAges={availChildAges}
                    onChange={(a, kids) => { setAvailGuests(a); setAvailChildAges(kids); }}
                    triggerClassName="flex w-full items-center gap-3 border border-sand-border bg-white px-3 py-2.5 text-left text-sm"
                  />
                </div>
                <button
                  onClick={checkAvailability}
                  disabled={refetching}
                  data-testid="check-availability-button"
                  className="mt-2 flex w-full items-center justify-center gap-2 bg-petrol py-2.5 text-sm font-semibold text-white transition-colors hover:bg-petrol/90 disabled:cursor-not-allowed disabled:opacity-70"
                >
                  {refetching ? (
                    <><Loader2 size={15} className="animate-spin" /> {t("detail.checking")}</>
                  ) : (
                    <><Search size={15} /> {t("detail.check_availability")}</>
                  )}
                </button>
              </div>

              {availCi && availCo && !p.live_pricing && !p.booking_live && (
                <p data-testid="not-live-note" className="mt-3 border-l-2 border-gold bg-gold/10 px-3 py-2 text-xs text-petrol/70">
                  {t("detail.not_live_note")}
                </p>
              )}

              <h3 className="overline mt-6 mb-3 text-petrol/60">{t("detail.provider_table")}</h3>
              <div data-testid="provider-comparison-table" className="flex flex-col gap-2">
                {isFlash && (
                  <div
                    data-testid="provider-row-flash"
                    className="flex items-center justify-between rounded-xl border border-terracotta bg-terracotta/10 px-4 py-3"
                  >
                    <span className="flex items-center gap-2 text-sm font-semibold text-terracotta">
                      <span className="h-2.5 w-2.5 rounded-full bg-terracotta" />
                      {lang === "it" ? "Offerta lampo" : lang === "hr" ? "Brza ponuda" : "Flash deal"}
                      <Check size={13} strokeWidth={3} />
                    </span>
                    <span className="flex items-center gap-3">
                      <span className="font-mono font-bold text-terracotta">{euro(p.flash_deal.deal_price)}</span>
                      <button
                        data-testid="book-deal-button-flash"
                        onClick={() => book("flash")}
                        className="flex items-center gap-1 rounded-none bg-terracotta px-3 py-1.5 text-xs font-semibold text-white transition-colors hover:bg-terracotta-hover"
                      >
                        {t("detail.book")} <ChevronRight size={13} />
                      </button>
                    </span>
                  </div>
                )}
                {providerRows.map((row) => {
                  const isBest = row.id === p.best_provider && !isFlash;
                  const soldOut = row.price == null;
                  return (
                    <div
                      key={row.id}
                      data-testid={`provider-row-${row.id}`}
                      className={`flex items-center justify-between rounded-xl border px-4 py-3 ${
                        isBest ? "border-emerald-500 bg-emerald-50" : "border-sand-border bg-white"
                      }`}
                    >
                      <span className="flex items-center gap-2 text-sm font-medium text-petrol">
                        {row.id === "nuitee" ? (
                          <BrandTag size="md" compact />
                        ) : (
                          <>
                            <span className="h-2.5 w-2.5 rounded-full" style={{ background: PROVIDER_META[row.id].color }} />
                            {providerName(row.id)}
                          </>
                        )}
                        {((row.id === "nuitee" && p.live_pricing) || (row.id === "booking" && p.booking_live)) && (
                          <span data-testid="live-price-badge" className="flex items-center gap-1 rounded bg-emerald-100 px-1.5 py-0.5 text-[0.6rem] font-bold uppercase text-emerald-700">
                            <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-emerald-500" />
                            {t("card.live")}
                          </span>
                        )}
                        {isBest && <Check size={13} className="text-emerald-600" strokeWidth={3} />}
                      </span>
                      {soldOut ? (
                        <span className="text-xs italic text-petrol/40">{t("detail.coming_soon")}</span>
                      ) : (
                        <span className="flex items-center gap-3">
                          <span className={`font-mono ${isBest ? "font-bold text-emerald-700" : "text-petrol"}`}>{euro(row.price)}</span>
                          <button
                            data-testid={`book-deal-button-${row.id}`}
                            onClick={() => book(row.id)}
                            className="flex items-center gap-1 rounded-none bg-petrol px-3 py-1.5 text-xs font-semibold text-white transition-colors hover:bg-terracotta"
                          >
                            {t("detail.book")} <ChevronRight size={13} />
                          </button>
                        </span>
                      )}
                    </div>
                  );
                })}
              </div>

              <button
                data-testid="book-deal-button"
                onClick={() => book(isFlash ? "flash" : p.best_provider)}
                className="mt-5 w-full rounded-none bg-terracotta py-3.5 font-semibold text-white transition-colors hover:bg-terracotta-hover"
              >
                {t("detail.book")} · {euro(p.best_price)}
              </button>
              {affiliate?.awin_publisher && affiliate?.awin_holidu_mid && (
                <a
                  data-testid="holidu-affiliate-link"
                  href={`https://www.awin1.com/cread.php?awinmid=${affiliate.awin_holidu_mid}&awinaffid=${affiliate.awin_publisher}&ued=${encodeURIComponent("https://www.holidu.it/search?query=Umag " + p.name)}`}
                  target="_blank"
                  rel="noreferrer sponsored"
                  className="mt-3 flex w-full items-center justify-center gap-2 rounded-none border border-petrol py-3 text-sm font-semibold text-petrol transition-colors hover:bg-petrol hover:text-sand"
                >
                  {t("detail.search_holidu")}
                </a>
              )}
            </div>
          </div>
        </div>
      </div>
      <div className="mt-16" />

      {/* Full-screen gallery lightbox */}
      {galleryOpen && (
        <div className="fixed inset-0 z-[60] flex flex-col bg-petrol/95 backdrop-blur-sm" data-testid="gallery-lightbox">
          <div className="flex items-center justify-between px-5 py-4 text-white">
            <span className="font-mono text-sm">{active + 1} / {imgs.length}</span>
            <button onClick={() => setGalleryOpen(false)} data-testid="gallery-close" aria-label="close gallery"
              className="flex h-10 w-10 items-center justify-center rounded-none bg-white/10 transition-colors hover:bg-white/25">
              <X size={20} />
            </button>
          </div>
          <div className="relative flex flex-1 items-center justify-center overflow-hidden px-4">
            {imgs.length > 1 && (
              <button onClick={prevPhoto} data-testid="lightbox-prev" aria-label="previous photo"
                className="absolute left-4 flex h-12 w-12 items-center justify-center rounded-none bg-white/10 text-white transition-colors hover:bg-white/25">
                <ChevronLeft size={26} />
              </button>
            )}
            <motion.img
              key={active}
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.3 }}
              src={imgs[active]} alt={p.name}
              loading="lazy" decoding="async"
              className="max-h-[74vh] max-w-full object-contain"
            />
            {imgs.length > 1 && (
              <button onClick={nextPhoto} data-testid="lightbox-next" aria-label="next photo"
                className="absolute right-4 flex h-12 w-12 items-center justify-center rounded-none bg-white/10 text-white transition-colors hover:bg-white/25">
                <ChevronRight size={26} />
              </button>
            )}
          </div>
          <div className="flex gap-2 overflow-x-auto px-5 py-4">
            {imgs.map((img, i) => (
              <button
                key={i} onClick={() => setActive(i)} data-testid={`lightbox-thumb-${i}`}
                className={`h-14 w-20 flex-shrink-0 overflow-hidden rounded-none border-2 transition-all ${active === i ? "border-gold" : "border-transparent opacity-60 hover:opacity-100"}`}
              >
                <img src={bookingImg(img, "square240")} alt="" loading="lazy" decoding="async" className="h-full w-full object-cover" />
              </button>
            ))}
          </div>
        </div>
      )}

      <BookingModal
        open={bookingOpen}
        onOpenChange={setBookingOpen}
        property={p}
        checkIn={params.get("check_in") || ""}
        checkOut={params.get("check_out") || ""}
        guests={Number(params.get("guests")) || 2}
        childrenAges={params.get("children_ages") || ""}
      />
      <Footer />
    </div>
  );
}
